/**
 * @file BinGenerateFields.tsx
 * @description Поля генератора стеллажа и превью адресов (phase19.14, W14).
 *
 * Отделены от диалога: диалог отвечает за валидацию и два запроса (dryRun и
 * создание), поля — только за ввод и показ того, что получится.
 *
 * Любая правка параметров сбрасывает превью (`onChange` обнуляет его в
 * родителе): создавать по устаревшему расчёту нельзя.
 */

import { Label } from "../../ui/label";
import { MAX_BIN_SEGMENT, type GenerateBinsResultDto } from "../../../lib/api-bins";

// w-full + min-w-0: у input своя внутренняя ширина (~20 символов) и
// min-width:auto, без них строка из трёх полей не ужимается и даёт
// горизонтальный скролл на 360px.
const FIELD =
  "min-h-[56px] w-full min-w-0 rounded-xl border border-on-surface/15 bg-surface-lowest px-3 text-base";

interface Props {
  rack: string;
  shelves: string;
  cells: string;
  disabled: boolean;
  onChange: (patch: { rack?: string; shelves?: string; cells?: string }) => void;
  /** Результат dryRun; null — превью ещё не считали. */
  preview: GenerateBinsResultDto | null;
}

export function BinGenerateFields({
  rack,
  shelves,
  cells,
  disabled,
  onChange,
  preview,
}: Props) {
  return (
    <>
      <div className="flex gap-2">
        <div className="flex w-20 min-w-0 flex-col gap-1.5">
          <Label htmlFor="gen-rack">Стеллаж</Label>
          <input
            id="gen-rack"
            value={rack}
            maxLength={1}
            onChange={(e) => onChange({ rack: e.target.value.toUpperCase() })}
            disabled={disabled}
            className={`${FIELD} font-mono uppercase`}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Label htmlFor="gen-shelves">Полок</Label>
          <input
            id="gen-shelves"
            type="number"
            min={1}
            max={MAX_BIN_SEGMENT}
            value={shelves}
            onChange={(e) => onChange({ shelves: e.target.value })}
            disabled={disabled}
            className={FIELD}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Label htmlFor="gen-cells">Ячеек на полке</Label>
          <input
            id="gen-cells"
            type="number"
            min={1}
            max={MAX_BIN_SEGMENT}
            value={cells}
            onChange={(e) => onChange({ cells: e.target.value })}
            disabled={disabled}
            className={FIELD}
          />
        </div>
      </div>

      {preview ? (
        <div className="rounded-xl bg-surface-low p-3 text-sm">
          <div className="font-medium">
            Будет создано: {preview.created.length}
            {preview.skipped.length ? ` · уже есть: ${preview.skipped.length}` : ""}
          </div>
          <div className="mt-1 break-words font-mono text-xs text-on-surface-variant">
            {preview.created.slice(0, 24).join(", ")}
            {preview.created.length > 24 ? " …" : ""}
          </div>
          {preview.created.length === 0 ? (
            <div className="mt-1 text-xs text-on-surface-variant">
              Все адреса диапазона уже заведены — создавать нечего.
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
